import { reactive } from 'vue';

// 与加解密一致：仅正式域名 mp.infiniax.com 启用版本更新提示
const UPDATE_HOSTS = ['mp.infiniax.com', 'mp-s2t.infiniax.com'];
const enableAppUpdates = UPDATE_HOSTS.indexOf(location.hostname) !== -1;

export const appUpdateState = reactive({ available: false, applying: false, error: false });

let registration;
let reloadTimer;
let updatePending = false;
let remindAfter = 0;

export function postponeAppUpdate() {
  remindAfter = Date.now() + 30 * 60 * 1000;
  appUpdateState.available = false;
}

function offerUpdate() {
  updatePending = true;
  if (Date.now() >= remindAfter) appUpdateState.available = true;
}

export function checkForAppUpdate() {
  return registration?.update().catch(() => {});
}

export async function applyAppUpdate() {
  if (appUpdateState.applying) return;
  appUpdateState.error = false;
  if (!navigator.onLine) {
    appUpdateState.error = true;
    return;
  }
  appUpdateState.applying = true;
  if (registration?.waiting) {
    reloadTimer = setTimeout(() => {
      appUpdateState.applying = false;
      appUpdateState.error = true;
    }, 15000);
    registration.waiting.postMessage({ type: 'ACTIVATE_UPDATE' });
  } else if (navigator.serviceWorker.controller) {
    window.location.reload();
  } else {
    appUpdateState.applying = false;
    appUpdateState.error = true;
  }
}

export async function startAppUpdates() {
  if (!enableAppUpdates || !import.meta.env.PROD || !window.isSecureContext || !('serviceWorker' in navigator)) return () => {};
  let previousController = navigator.serviceWorker.controller;
  let lastCheck = 0;
  let disposed = false;
  const watchedWorkers = new Map();
  const showWaiting = () => {
    if ((registration.waiting && registration.active) || updatePending) offerUpdate();
  };
  const controllerChanged = () => {
    const controller = navigator.serviceWorker.controller;
    if (appUpdateState.applying) {
      clearTimeout(reloadTimer);
      window.location.reload();
    } else if (previousController && controller !== previousController) {
      offerUpdate();
    }
    previousController = controller;
  };
  const watchInstalling = () => {
    const worker = registration.installing;
    if (!worker || watchedWorkers.has(worker)) return;
    const changed = () => {
      if (worker.state === 'installed') showWaiting();
    };
    watchedWorkers.set(worker, changed);
    worker.addEventListener('statechange', changed);
  };
  const check = () => {
    if (disposed || document.visibilityState === 'hidden' || !navigator.onLine || Date.now() - lastCheck < 30000) return;
    showWaiting();
    lastCheck = Date.now();
    checkForAppUpdate();
  };
  navigator.serviceWorker.addEventListener('controllerchange', controllerChanged);
  try {
    const base = new URL('./', document.baseURI);
    registration = await navigator.serviceWorker.register(new URL('sw.js', base), { scope: base.pathname, updateViaCache: 'none' });
    showWaiting();
    watchInstalling();
    registration.addEventListener('updatefound', watchInstalling);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
    window.addEventListener('vite:preloadError', checkForAppUpdate);
    const interval = setInterval(check, 5 * 60 * 1000);
    check();
    return () => {
      disposed = true;
      clearInterval(interval);
      clearTimeout(reloadTimer);
      registration.removeEventListener('updatefound', watchInstalling);
      navigator.serviceWorker.removeEventListener('controllerchange', controllerChanged);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      window.removeEventListener('online', check);
      window.removeEventListener('vite:preloadError', checkForAppUpdate);
      for (const [worker, changed] of watchedWorkers) worker.removeEventListener('statechange', changed);
    };
  } catch {
    navigator.serviceWorker.removeEventListener('controllerchange', controllerChanged);
    return () => {};
  }
}
